import { parseWithZod } from '@conform-to/zod';
import { useCallback, useEffect, useState } from 'react';
import { type Params, data, useFetcher } from 'react-router';
import { useAuthenticityToken } from 'remix-utils/csrf/react';
import { z } from 'zod';
import { zx } from 'zodix';

import { getHints } from '~/components/client-hints';
import { createEventTicketPayment } from '~/domain/events/handlers/create-event-ticket-payment.server';
import { deleteEventTicket } from '~/domain/events/handlers/delete-event-ticket.server';
import { type AthleteEvent } from '~/domain/events/handlers/get-athlete-events.server';
import { getEventForAthlete } from '~/domain/events/handlers/get-event-for-athlete.server';
import { CreateEventPaymentError } from '~/domain/events/models/create-event-payment-error';
import { type EventRegistrationHouseholdData } from '~/domain/households/queries/get-household-members-for-event-registration.server';
import { type Session } from '~/domain/users/models/session';
import { BadRequestError, MethodNotAllowedError } from '~/types/errors';
import { requireSession } from '~/utils/auth.server';
import { validateCSRF } from '~/utils/csrf.server';
import { handleDomainError } from '~/utils/errors.server';
import { useFetcherWithPromise } from '~/utils/fetcher-with-promise';
import { createToastHeaders } from '~/utils/toast.server';

import { submit } from '../admin+/gyms+/$gymId+/events+/event-submitter.server';
import { type PromoCode } from '../gym+/$slug+/components/purchase-modal';
import { type PromoCodeValidationResponse } from '../gym+/$slug+/resources+/validate-promo';

import { type loader as eventDetailsLoader } from './$eventId';
import { type Route } from './+types/$eventId.tickets';

export async function loader({ request, params }: Route.LoaderArgs) {
  const session = await requireSession(request);
  const { eventId } = zx.parseParams(params, {
    eventId: z.coerce.number(),
  });

  const result = await getEventForAthlete(session, eventId);

  return result.mapErr(handleDomainError).unwrap();
}

const paramsSchema = z.object({
  eventId: z.coerce.number(),
});

const bodySchema = z.object({
  promoCode: z.string().optional(),
  memberIds: z.array(z.string()).optional(),
});

export type PostActionReturn = Awaited<ReturnType<typeof handlePost>>;
export type DeleteActionReturn = Awaited<ReturnType<typeof handleDelete>>;

export async function action({ request, params }: Route.ActionArgs) {
  const session = await requireSession(request);

  switch (request.method) {
    case 'POST':
      return await handlePost(session, request, params);
    case 'DELETE':
      return await handleDelete(session, request, params);
    default:
      throw new MethodNotAllowedError('Method not allowed');
  }
}

async function handlePost(session: Session, request: Request, params: Params) {
  const { eventId } = zx.parseParams(params, paramsSchema);
  const { timeZone } = getHints(request);

  const formData = await request.formData();
  await validateCSRF(formData, request.headers);

  // Handle memberIds for household contexts
  const memberIds = formData.getAll('memberIds');
  const hasMemberIdsField = formData.has('memberIds');
  // If memberIds field is not present, it means single registration
  // If memberIds field is present but empty, it means household selection was offered but no members selected
  const parsedMemberIds = hasMemberIdsField
    ? memberIds.length > 0
      ? (memberIds as string[])
      : []
    : undefined;

  const submission = parseWithZod(formData, { schema: bodySchema });
  if (submission.status !== 'success') {
    return submission.reply();
  }
  const { promoCode } = submission.value;

  const result = await createEventTicketPayment(
    session,
    timeZone,
    eventId,
    promoCode,
    parsedMemberIds,
  );

  if (result.isErr()) {
    const error = result.error;
    // If the error is a BadRequestError, show a toast instead of throwing so the user knows something went wrong
    if (error instanceof BadRequestError) {
      return data(
        { error: error.message },
        {
          headers: await createToastHeaders({
            title: 'Unable to Register',
            description: error.message,
          }),
        },
      );
    }
    handleDomainError(error);
  }

  const response = result.value;

  // if it's a free ticket, we can show a success toast immediately. Otherwise
  // we have to go through the payment flow first and toast on the way back from
  // the return route.
  if ('ticketType' in response && response.ticketType === 'free') {
    const rsvpMessage = `${response.eventName} has been added to your Events list.`;
    const waiverMessage = response.hasWaiver
      ? ' Your signed waivers can be viewed under My Account.'
      : '';

    submit(eventId, 'rsvp-created');

    return data(response, {
      headers: await createToastHeaders({
        title: 'Success!',
        description: `${rsvpMessage}${waiverMessage}`,
      }),
    });
  }

  return response;
}

async function handleDelete(session: Session, _request: Request, params: Params) {
  const { eventId } = zx.parseParams(params, paramsSchema);

  const result = await deleteEventTicket(session, eventId);

  const deleteResult = result.mapErr(handleDomainError).unwrap();

  const responseInit = deleteResult.deletedEvent
    ? {
        headers: await createToastHeaders({
          title: 'Success!',
          description: `${deleteResult.deletedEvent.name} has been removed from your Events list.`,
        }),
      }
    : undefined;

  if (deleteResult.deletedEvent) {
    submit(eventId, 'rsvp-deleted');
  }

  return data(deleteResult, responseInit);
}

export const enum ButtonAction {
  BuyTicket,
  GetFreeTicket,
  DeleteFreeTicket,
}

type EventDetails = Pick<
  AthleteEvent,
  'id' | 'freeEvent' | 'userHasTicket' | 'totalTickets' | 'ticketsPurchased' | 'waiverRequired'
> & { waiverHtml: string | null };

const getButtonAction = (event: EventDetails) => {
  if (!event.freeEvent) {
    return ButtonAction.BuyTicket;
  }
  return !event.userHasTicket ? ButtonAction.GetFreeTicket : ButtonAction.DeleteFreeTicket;
};

export function useEventTicketing({
  event,
  gymSlugOrId,
  householdData,
  eventPrice,
}: {
  event: EventDetails;
  gymSlugOrId: string;
  householdData: EventRegistrationHouseholdData | null;
  eventPrice: number | null;
}) {
  const [showingPaymentModal, setShowingPaymentModal] = useState(false);
  const [showingWaiverModal, setShowingWaiverModal] = useState(false);
  const [showingHouseholdModal, setShowingHouseholdModal] = useState(false);
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);
  const [householdSelectionOffered, setHouseholdSelectionOffered] = useState(false);
  const [stripeClientSecret, setStripeClientSecret] = useState<string | null>(null);
  const [promoCode, setPromoCode] = useState<PromoCode | undefined>();
  const [amount, setAmount] = useState<number | undefined>();
  const [amountBelowMinimum, setAmountBelowMinimum] = useState(false);
  const [promoCodeInvalid, setPromoCodeInvalid] = useState(false);
  const [paymentId, setPaymentId] = useState<string | undefined>();

  const createEventTicketFetcher = useFetcher<PostActionReturn | DeleteActionReturn>();
  const createUserEventWaiverFetcher = useFetcher();
  const getEventFetcher = useFetcherWithPromise<typeof eventDetailsLoader>();
  const validatePromoFetcher = useFetcherWithPromise<PromoCodeValidationResponse>();
  const csrf = useAuthenticityToken();

  const isSubmitting = createEventTicketFetcher.state !== 'idle';

  // Check if household members were deleted
  const householdMembersDeleted =
    createEventTicketFetcher.data != null &&
    'householdMembersDeleted' in createEventTicketFetcher.data &&
    createEventTicketFetcher.data.householdMembersDeleted === true;

  const eventSoldOutError =
    createEventTicketFetcher.data != null &&
    'error' in createEventTicketFetcher.data &&
    createEventTicketFetcher.data.error === CreateEventPaymentError.EventSoldOut;

  const buttonAction = getButtonAction(event);

  // All the derived state for attendance...
  const availableTickets = event.totalTickets
    ? event.totalTickets - (event.ticketsPurchased ?? 0)
    : null;
  const eventSoldOut = (availableTickets !== null && availableTickets <= 0) || eventSoldOutError;

  // Calculate amount based on ticket count and event price
  // This ensures the amount is always correct, even if server response is delayed or incorrect
  // Note: We only recalculate when there's no promo code, as promo code calculations require server-side logic
  useEffect(() => {
    const ticketCount = selectedMemberIds.length > 0 ? selectedMemberIds.length : 1;
    if (!event.freeEvent && eventPrice !== null && promoCode === undefined) {
      const calculatedAmount = eventPrice * ticketCount;
      setAmount((currentAmount) => {
        // Always recalculate if ticket count changed, or if amount is undefined
        // This ensures we show the correct total even if server response hasn't arrived yet
        if (currentAmount === undefined || currentAmount !== calculatedAmount) {
          return calculatedAmount;
        }
        return currentAmount;
      });
    }
  }, [event.freeEvent, eventPrice, selectedMemberIds.length, promoCode]);

  // Launch the Stripe modal for paid events once the ticket and payment intent have been created
  useEffect(() => {
    if (
      !event.freeEvent &&
      createEventTicketFetcher.state === 'idle' &&
      createEventTicketFetcher.data &&
      'clientSecret' in createEventTicketFetcher.data &&
      (createEventTicketFetcher.data.clientSecret || createEventTicketFetcher.data.paymentId) &&
      !eventSoldOutError
    ) {
      const serverAmount = createEventTicketFetcher.data.amount;

      setStripeClientSecret(createEventTicketFetcher.data.clientSecret ?? null);
      setPromoCode(createEventTicketFetcher.data.promoCode);
      // Always use the server amount as it includes promo code discounts and is the source of truth
      setAmount(serverAmount);
      setAmountBelowMinimum(createEventTicketFetcher.data.isBelowMinimum);
      setPromoCodeInvalid(createEventTicketFetcher.data.promoCodeInvalid);
      setPaymentId(createEventTicketFetcher.data.paymentId);
      setShowingPaymentModal(true);
    }
  }, [
    eventSoldOutError,
    createEventTicketFetcher,
    event.freeEvent,
    eventPrice,
    selectedMemberIds.length,
  ]);

  const handleTicketAction = useCallback(
    (waiverFullName?: string, skipHouseholdModal = false, memberIdsOverride?: string[]) => {
      // Use override memberIds if provided, otherwise use state
      // Gets around the issue of the selectedMemberIds state not being updated immediately
      const memberIdsToUse = memberIdsOverride ?? selectedMemberIds;

      // Show household modal first if user is an adult in household, has other members, and not deleting ticket
      if (
        !skipHouseholdModal &&
        householdData?.isAdultInHousehold &&
        householdData.members.length > 1 &&
        buttonAction !== ButtonAction.DeleteFreeTicket
      ) {
        setHouseholdSelectionOffered(true);
        setShowingHouseholdModal(true);
        return;
      }

      // Check if only other adults (not self, not children) are selected
      // Other adults will need to sign their own waivers at check-in
      const onlyOtherAdultsSelected =
        householdData &&
        memberIdsToUse.length > 0 &&
        !memberIdsToUse.includes(householdData.authenticatedMemberId ?? '') &&
        memberIdsToUse.every((id) => {
          const member = householdData.members.find((m) => m.id === id);
          return member?.memberType === 'adult';
        });

      if (
        event.waiverRequired &&
        event.waiverHtml &&
        !waiverFullName &&
        !onlyOtherAdultsSelected &&
        buttonAction !== ButtonAction.DeleteFreeTicket
      ) {
        setShowingWaiverModal(true);
        return;
      }

      // Only submit waiver signature if not registering only other adults
      if (waiverFullName && !onlyOtherAdultsSelected) {
        const formData = new FormData();
        formData.append('fullName', waiverFullName);
        formData.append('csrf', csrf);

        if (memberIdsToUse.length > 0) {
          memberIdsToUse.forEach((memberId) => {
            formData.append('memberIds', memberId);
          });
        }
        createUserEventWaiverFetcher.submit(formData, {
          method: 'POST',
          action: `/events/${event.id}/waiver`,
        });
      }

      const ticketFormData = new FormData();
      ticketFormData.append('csrf', csrf);

      // Include memberIds in form data if household selection was offered, even if empty
      // This allows backend to distinguish between household selection vs single registration
      if (householdSelectionOffered || memberIdsToUse.length > 0) {
        memberIdsToUse.forEach((memberId) => {
          ticketFormData.append('memberIds', memberId);
        });
      }
      createEventTicketFetcher.submit(ticketFormData, {
        method: buttonAction === ButtonAction.DeleteFreeTicket ? 'DELETE' : 'POST',
        action: `/events/${event.id}/tickets`,
      });
    },
    [
      buttonAction,
      createEventTicketFetcher,
      createUserEventWaiverFetcher,
      csrf,
      event.id,
      event.waiverHtml,
      event.waiverRequired,
      householdData,
      householdSelectionOffered,
      selectedMemberIds,
    ],
  );

  const handlePaymentModalClose = useCallback(() => {
    setShowingPaymentModal(false);
    setShowingWaiverModal(false);
    setPromoCode(undefined);
    setAmount(undefined);
    setAmountBelowMinimum(false);
    setPromoCodeInvalid(false);
    setPaymentId(undefined);
  }, []);

  const handleWaiverModalClose = useCallback(() => {
    setShowingWaiverModal(false);
  }, []);

  const handleWaiverAccept = useCallback(
    (fullName: string, memberIds?: string[]) => {
      setShowingWaiverModal(false);
      if (memberIds) {
        setSelectedMemberIds(memberIds);
        handleTicketAction(fullName, true, memberIds);
      } else {
        handleTicketAction(fullName, true);
      }
    },
    [handleTicketAction],
  );

  const handleCreateEventPaymentPreCheck = useCallback(async (): Promise<string | null> => {
    const data = await getEventFetcher.submit(null, {
      method: 'GET',
      action: `/events/${event.id}`,
    });

    if (!data || !data.eventDetails?.hasAvailableTicketCapacity) {
      return 'This event is now sold out.';
    }

    if (promoCode) {
      const promoData = await validatePromoFetcher.submit(
        { csrf, promoCodeName: promoCode.name },
        {
          method: 'POST',
          action: `/gym/${gymSlugOrId}/resources/validate-promo`,
        },
      );

      if (!promoData?.valid) {
        return 'This promo code is no longer valid.';
      }
    }

    return null;
  }, [csrf, event.id, getEventFetcher, gymSlugOrId, promoCode, validatePromoFetcher]);

  const handlePromoCodeSubmit = useCallback(
    (promoCode: string) => {
      const promoFormData = new FormData();
      promoFormData.append('csrf', csrf);
      promoFormData.append('promoCode', promoCode);

      if (selectedMemberIds.length > 0) {
        selectedMemberIds.forEach((memberId) => {
          promoFormData.append('memberIds', memberId);
        });
      }
      createEventTicketFetcher.submit(promoFormData, {
        method: 'POST',
        action: `/events/${event.id}/tickets`,
      });
    },
    [createEventTicketFetcher, csrf, event.id, selectedMemberIds],
  );

  const handleHouseholdModalClose = useCallback(() => {
    setShowingHouseholdModal(false);
  }, []);

  const handleHouseholdModalContinue = useCallback(
    (memberIds: string[]) => {
      setSelectedMemberIds(memberIds);
      setShowingHouseholdModal(false);

      if (
        event.waiverRequired &&
        event.waiverHtml &&
        buttonAction !== ButtonAction.DeleteFreeTicket
      ) {
        setShowingWaiverModal(true);
      } else {
        handleTicketAction(undefined, true, memberIds);
      }
    },
    [event.waiverHtml, event.waiverRequired, buttonAction, handleTicketAction],
  );

  return {
    // State
    showingPaymentModal,
    showingWaiverModal,
    showingHouseholdModal,
    selectedMemberIds,
    stripeClientSecret,
    promoCode,
    amount,
    amountBelowMinimum,
    promoCodeInvalid,
    paymentId,
    isSubmitting,
    eventSoldOutError,
    availableTickets,
    eventSoldOut,
    buttonAction,
    householdMembersDeleted,
    // Handlers
    handleTicketAction,
    handlePaymentModalClose,
    handleWaiverModalClose,
    handleWaiverAccept,
    handleCreateEventPaymentPreCheck,
    handlePromoCodeSubmit,
    handleHouseholdModalClose,
    handleHouseholdModalContinue,
  };
}
